/**
 * CentrifugalCompressorCalculator.js (FIXED VERSION)
 * 
 * Detailed simulation of a multi-stage Centrifugal Compressor and its 
 * Gas Turbine driver (Brayton Cycle).
 * 
 * IMPROVEMENTS:
 * - T-s diagrams now correctly show:
 *   • Isentropic path as VERTICAL line (constant entropy)
 *   • Actual path as CURVED line (entropy increases)
 *   • Shaded area between: represents irreversibility
 * - Proper entropy calculation from equation of state
 * - Work verification: enthalpy change = polytropic path
 */

import React, { useState, useEffect } from 'react';
import { COMPONENTS, R, calculateEntropy } from '../fluid-properties';
import { mixtureCp_J_molK, mixtureZ, mixtureMW, COMP_LIST, N_COMP } from './canvas/canvasThermo';
import { 
  Line, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';

export default function CentrifugalCompressorCalculator({ initialComposition }) {
  // Build composition array aligned with COMP_LIST so thermo functions get correct indices
  // COMP_LIST order: CH4, C2, C3, C4, C7, C8, C9, N2, CO2, H2S, H2O (N_COMP=11)
  const z = useState(() => {
    const arr = new Float64Array(N_COMP);
    // Map display names to COMP_LIST indices
    const nameMap = {
      'Methane': 0, 'Ethane': 1, 'Propane': 2, 'n-Butane': 3,
      'n-Heptane': 4, 'n-Octane': 5, 'n-Nonane': 6,
      'Nitrogen': 7, 'Carbon Dioxide': 8, 'Hydrogen Sulfide': 9, 'Water': 10,
      // Legacy names from old flowsheets
      'n-Pentane': 4, 'n-Hexane': 5,
    };
    let sum = 0;
    Object.values(initialComposition).forEach(v => sum += (v || 0));
    if (sum <= 0) sum = 1;
    Object.entries(initialComposition).forEach(([name, val]) => {
      const idx = nameMap[name];
      if (idx !== undefined) arr[idx] += (val || 0) / sum;
    });
    // If no composition provided, default to lean natural gas
    const total = arr.reduce((s, v) => s + v, 0);
    if (total < 0.01) {
      arr[0] = 0.85; arr[1] = 0.08; arr[2] = 0.04; arr[3] = 0.02; arr[7] = 0.01;
    }
    return arr;
  })[0];

  // --- State: Compressor Parameters ---
  const [compParams, setCompParams] = useState({
    Tin_C: 25,
    Pin_bar: 30,
    Pout_bar: 90,
    Flow_e3m3d: 1000,
    eta_poly: 0.78,
    stages: 2,
    intercoolT_C: 40,
    useIntercooler: true,  // false = no intercooling, gas heats continuously stage to stage
  });

  // --- State: Gas Turbine Parameters (Brayton Cycle) ---
  const [gtParams, setCompGT] = useState({
    Tamb_C: 15,
    Pamb_bar: 1.01325,
    PR_gt: 18,          // GT compressor pressure ratio
    TIT_C: 1150,        // Turbine Inlet Temp
    eta_comp: 0.85,     // GT compressor isentropic eff
    eta_turb: 0.88,     // GT turbine isentropic eff
    eta_mech: 0.97,     // Mechanical efficiency (GT to Load)
    LHV_MJ_kg: 48.0     // Typical fuel gas LHV
  });

  const [results, setResults] = useState(null);

  const calculate = () => {
    // 1. Thermodynamic Properties
    const MW = mixtureMW(z);
    
    // Standard Conditions: 15°C, 1.01325 bar
    const Tstd = 288.15;
    const Pstd = 1.01325;
    const Vm_std = (R.BAR_L_MOL_K * Tstd) / Pstd; // ~23.644 m3/kmol
    
    // Convert e3m3/d to kmol/h:
    const flow_kmolh = (compParams.Flow_e3m3d * 1000) / 24 / Vm_std;

    // 2. Compressor Calculation (FIXED T-s paths)
    const compResults = [];
    let currentT = compParams.Tin_C + 273.15;
    let currentP = compParams.Pin_bar;
    const PR_per_stage = Math.pow(compParams.Pout_bar / compParams.Pin_bar, 1 / compParams.stages);
    
    let totalPower_kW = 0;

    for (let i = 0; i < compParams.stages; i++) {
      const stagePin = currentP;
      const stagePout = stagePin * PR_per_stage;
      const stageTin = currentT;

      // Fluid properties at inlet
      const { Z: Z_in } = mixtureZ(stageTin, stagePin, z);
      const Cp = mixtureCp_J_molK(stageTin, z);
      const Cv = Cp - R.J_MOL_K * Z_in;
      const gamma = Cp / Math.max(Cv, 10);
      
      // Polytropic index: n/(n-1) = (gamma/(gamma-1)) / eta_poly
      const n_inv = ((gamma - 1) / gamma) / compParams.eta_poly;
      const stageTout = stageTin * Math.pow(stagePout / stagePin, n_inv);
      
      // Entropy at key points
      const S_1 = calculateEntropy(stageTin, stagePin, COMP_LIST, z);
      const S_2s = S_1; // Isentropic: constant entropy
      const S_2 = calculateEntropy(stageTout, stagePout, COMP_LIST, z);
      
      const stagePower_kW = flow_kmolh * Cp * (stageTout - stageTin) / 3600;
      totalPower_kW += stagePower_kW;

      // ──── Path 1→2s: ISENTROPIC (constant entropy, vertical on T-s diagram) ────
      const path_1_2s = Array.from({ length: 26 }, (_, j) => {
        const ratio = j / 25;
        // From P1 to P2 at constant entropy:
        // T = T1 * (P/P1)^((γ-1)/γ)  (isentropic relation)
        const p = stagePin * Math.pow(stagePout / stagePin, ratio);
        const t = stageTin * Math.pow(p / stagePin, (gamma - 1) / gamma);
        // Entropy stays constant (by definition)
        const s = S_1;
        return { 
          s, 
          T: t - 273.15,
          type: 'isentropic'
        };
      });

      // ──── Path 1→2: ACTUAL (polytropic, curved on T-s diagram) ────
      const path_1_2 = Array.from({ length: 26 }, (_, j) => {
        const ratio = j / 25;
        // Polytropic path: T = T1 * (P/P1)^((γ-1)/(η_poly*γ))
        const p = stagePin * Math.pow(stagePout / stagePin, ratio);
        const m = ((gamma - 1) / gamma) / compParams.eta_poly;
        const t = stageTin * Math.pow(p / stagePin, m);
        const s = calculateEntropy(t, p, COMP_LIST, z);
        return { 
          s, 
          T: t - 273.15,
          type: 'actual'
        };
      });

      compResults.push({
        stage: i + 1,
        Tin: stageTin - 273.15,
        Pin: stagePin,
        Tout: stageTout - 273.15,
        Pout: stagePout,
        power: stagePower_kW,
        S_1,
        S_2s,
        S_2,
        T_1: stageTin,
        T_2s: stageTin * Math.pow(stagePout / stagePin, (gamma - 1) / gamma),
        T_2: stageTout,
        path_1_2s,
        path_1_2,
        // For verification:
        work_actual_kJ_kmol: Cp * (stageTout - stageTin),
        work_isentropic_kJ_kmol: Cp * (stageTin * Math.pow(stagePout / stagePin, (gamma - 1) / gamma) - stageTin),
        eta_isentropic: (stageTin * Math.pow(stagePout / stagePin, (gamma - 1) / gamma) - stageTin) / (stageTout - stageTin),
        entropy_increase_J_molK: S_2 - S_1,
        irreversibility_loss: (stageTout + 273.15) * (S_2 - S_1) // T*ΔS exergy loss (kJ/kmol)
      });

      // Carry forward to next stage — intercool if option enabled, otherwise carry hot discharge
      currentP = stagePout;
      if (i < compParams.stages - 1) {
        currentT = compParams.useIntercooler
          ? compParams.intercoolT_C + 273.15  // cooled back to intercooler outlet T
          : stageTout;                          // no cooling — next stage starts at hot discharge T
      } else {
        currentT = stageTout; // last stage: final discharge temperature
      }
    }

    // 3. Gas Turbine (Brayton Cycle) - with proper T-s paths
    // Working fluid is air (gamma=1.4, Cp=1.005 kJ/kgK)
    const Cp_air = 1.005; 
    const gamma_air = 1.4;
    const Tamb = gtParams.Tamb_C + 273.15;
    const TIT = gtParams.TIT_C + 273.15;
    const P1 = gtParams.Pamb_bar;
    const P2 = P1 * gtParams.PR_gt;

    // GT Compressor (Path 1→2)
    const T2s = Tamb * Math.pow(gtParams.PR_gt, (gamma_air-1)/gamma_air);
    const T2 = Tamb + (T2s - Tamb) / gtParams.eta_comp;
    const w_comp = Cp_air * (T2 - Tamb); // kJ/kg air

    // GT Combustor (Path 2→3) - constant pressure
    const P3 = P2;
    const T3 = TIT;

    // GT Turbine (Path 3→4)
    const T4s = TIT / Math.pow(gtParams.PR_gt, (gamma_air-1)/gamma_air);
    const T4 = TIT - (TIT - T4s) * gtParams.eta_turb;
    const w_turb = Cp_air * (TIT - T4); // kJ/kg air

    const w_net = w_turb - w_comp; // kJ/kg air
    const thermalEff = w_net / (Cp_air * (TIT - T2));
    
    // Required Air Flow
    const shaftPower_kW = totalPower_kW / gtParams.eta_mech;
    const m_dot_air = shaftPower_kW / w_net; // kg/s

    // Gas Turbine paths using air (Nitrogen proxy)
    const airComps = [COMPONENTS.NITROGEN];
    const airFracs = [1.0];
    
    // ──── Path 1→2: GT COMPRESSION (isentropic + actual) ────
    const S_1_gt = calculateEntropy(Tamb, P1, airComps, airFracs) / 28.01;

    const path12_isentropic = Array.from({ length: 26 }, (_, i) => {
      const ratio = i / 25;
      const p = P1 * Math.pow(P2 / P1, ratio);
      const t = Tamb * Math.pow(p / P1, (gamma_air - 1) / gamma_air);
      const s = S_1_gt; // Constant
      return { s, T: t - 273.15 };
    });

    const path12_actual = Array.from({ length: 26 }, (_, i) => {
      const ratio = i / 25;
      const p = P1 * Math.pow(P2 / P1, ratio);
      const m = ((gamma_air - 1) / gamma_air) / gtParams.eta_comp;
      const t = Tamb * Math.pow(p / P1, m);
      const s = calculateEntropy(t, p, airComps, airFracs) / 28.01;
      return { s, T: t - 273.15 };
    });
    
    // ──── Path 2→3: COMBUSTION (constant pressure) ────
    const S_3_gt = calculateEntropy(T3, P3, airComps, airFracs) / 28.01;

    const path23 = Array.from({ length: 26 }, (_, i) => {
      const t = T2 + (T3 - T2) * (i / 25);
      const s = calculateEntropy(t, P3, airComps, airFracs) / 28.01;
      return { s, T: t - 273.15 };
    });
    
    // ──── Path 3→4: EXPANSION (isentropic + actual) ────
    const S_3_gt_const = S_3_gt; // Isentropic constant entropy

    const path34_isentropic = Array.from({ length: 26 }, (_, i) => {
      const ratio = i / 25;
      const p = P2 * Math.pow(P1 / P2, ratio);
      const t = T3 * Math.pow(p / P2, (gamma_air - 1) / gamma_air);
      const s = S_3_gt_const; // Constant
      return { s, T: t - 273.15 };
    });

    const path34_actual = Array.from({ length: 26 }, (_, i) => {
      const ratio = i / 25;
      const p = P2 * Math.pow(P1 / P2, ratio);
      // Turbine actual polytropic: T = T3*(p/P2)^m where m = (γ-1)/γ * η_turb
      // (eta_turb < 1 means LESS temperature drop than isentropic → T4 > T4s)
      const m = ((gamma_air - 1) / gamma_air) * gtParams.eta_turb;
      const t = T3 * Math.pow(p / P2, m);
      const s = calculateEntropy(t, p, airComps, airFracs) / 28.01;
      return { s, T: t - 273.15 };
    });
    
    // ──── Path 4→1: EXHAUST (constant pressure) ────
    const path41 = Array.from({ length: 26 }, (_, i) => {
      const t = T4 + (Tamb - T4) * (i / 25);
      const s = calculateEntropy(t, P1, airComps, airFracs) / 28.01;
      return { s, T: t - 273.15 };
    });

    // Fuel Flow
    const fuelHeat_kW = shaftPower_kW / thermalEff;
    const m_dot_fuel = fuelHeat_kW / (gtParams.LHV_MJ_kg * 1000); // kg/s

    setResults({
      compStages: compResults,
      totalPower_kW,
      shaftPower_kW,
      MW,
      gt: {
        thermalEff,
        airFlow_kgs: m_dot_air,
        fuelFlow_kgs: m_dot_fuel,
        exhaustT_C: T4 - 273.15,
        T_comp_out_C: T2 - 273.15,
        // Store both isentropic and actual paths for visualization
        compression: {
          isentropic: path12_isentropic,
          actual: path12_actual,
          T1: Tamb - 273.15,
          T2s: T2s - 273.15,
          T2: T2 - 273.15,
          P1,
          P2
        },
        combustion: {
          path: path23,
          T2: T2 - 273.15,
          T3: T3 - 273.15,
          P: P3
        },
        expansion: {
          isentropic: path34_isentropic,
          actual: path34_actual,
          T3: T3 - 273.15,
          T4s: T4s - 273.15,
          T4: T4 - 273.15,
          P3: P2,
          P4: P1
        },
        exhaust: {
          path: path41,
          T4: T4 - 273.15,
          T1: Tamb - 273.15,
          P: P1
        }
      }
    });
  };

  useEffect(() => {
    calculate();
  }, [compParams, gtParams]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: 20, background: 'var(--color-background-primary)', borderRadius: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Input Column */}
        <div className="card">
          <h3 style={{ marginBottom: 15 }}>Centrifugal Compressor Params</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: 'Inlet Temp (°C)', key: 'Tin_C', min: -50, max: 100 },
              { label: 'Inlet Press (bar)', key: 'Pin_bar', min: 1, max: 200 },
              { label: 'Outlet Press (bar)', key: 'Pout_bar', min: 1, max: 500 },
              { label: 'Flow Rate (e3m3/d)', key: 'Flow_e3m3d', min: 1, max: 10000 },
              { label: 'Polytropic Eff', key: 'eta_poly', min: 0.5, max: 0.9, step: 0.01 },
              { label: 'Stages', key: 'stages', min: 1, max: 8, step: 1 },
            ].map(p => (
              <div key={p.key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <label>{p.label}</label>
                <input 
                  type="number" 
                  value={compParams[p.key]} 
                  step={p.step || 1}
                  onChange={e => setCompParams({...compParams, [p.key]: parseFloat(e.target.value)})}
                  style={{ width: 80 }}
                />
              </div>
            ))}
            {/* Intercooler toggle */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Intercooler</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={compParams.useIntercooler}
                  onChange={e => setCompParams({...compParams, useIntercooler: e.target.checked})}
                />
                {compParams.useIntercooler ? 'Enabled' : 'Disabled (adiabatic)'}
              </label>
            </div>
            {/* Show intercooler temp only when enabled */}
            {compParams.useIntercooler && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <label>Intercooler Temp (°C)</label>
                <input
                  type="number"
                  value={compParams.intercoolT_C}
                  step={1}
                  onChange={e => setCompParams({...compParams, intercoolT_C: parseFloat(e.target.value)})}
                  style={{ width: 80 }}
                />
              </div>
            )}
          </div>

          <h3 style={{ margin: '20px 0 15px' }}>Gas Turbine (Brayton Cycle)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: 'Ambient Temp (°C)', key: 'Tamb_C' },
              { label: 'Pressure Ratio', key: 'PR_gt' },
              { label: 'Turbine Inlet Temp (°C)', key: 'TIT_C' },
              { label: 'Comp Isentropic Eff', key: 'eta_comp' },
              { label: 'Turb Isentropic Eff', key: 'eta_turb' },
              { label: 'Mechanical Eff', key: 'eta_mech' },
            ].map(p => (
              <div key={p.key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <label>{p.label}</label>
                <input 
                  type="number" 
                  value={gtParams[p.key]} 
                  step={0.01}
                  onChange={e => setCompGT({...gtParams, [p.key]: parseFloat(e.target.value)})}
                  style={{ width: 80 }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Results Column */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <h3 style={{ marginBottom: 15 }}>Performance Results</h3>
            {results && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                <div className="result-item">
                  <strong>Total Process Power:</strong>
                  <span style={{ fontSize: 18, color: '#4caf50' }}> {results.totalPower_kW.toFixed(1)} kW</span>
                </div>
                <div className="result-item">
                  <strong>Required Shaft Power:</strong>
                  <span> {results.shaftPower_kW.toFixed(1)} kW</span>
                </div>
                
                <table className="composition-table">
                  <thead>
                    <tr>
                      <th>Stage</th>
                      <th>Tin (°C)</th>
                      <th>Tout (°C)</th>
                      <th>Pout (bar)</th>
                      <th>Power (kW)</th>
                      <th>η_is</th>
                      {compParams.useIntercooler && compParams.stages > 1 && <th>After</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {results.compStages.map((s, idx) => (
                      <tr key={s.stage}>
                        <td>{s.stage}</td>
                        <td>{s.Tin.toFixed(1)}</td>
                        <td>{s.Tout.toFixed(1)}</td>
                        <td>{s.Pout.toFixed(1)}</td>
                        <td>{s.power.toFixed(1)}</td>
                        <td>{(s.eta_isentropic * 100).toFixed(1)}%</td>
                        {compParams.useIntercooler && compParams.stages > 1 && (
                          <td style={{ color: idx < compParams.stages-1 ? '#2196f3' : '#888', fontSize: 11 }}>
                            {idx < compParams.stages-1 ? `→${compParams.intercoolT_C}°C` : '—'}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>

                <h4 style={{ marginTop: 10 }}>Gas Turbine Driver</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="metric">
                    <label>Thermal Efficiency</label>
                    <strong>{(results.gt.thermalEff * 100).toFixed(1)}%</strong>
                  </div>
                  <div className="metric">
                    <label>Fuel Flow</label>
                    <strong>{(results.gt.fuelFlow_kgs * 3600).toFixed(1)} kg/h</strong>
                  </div>
                  <div className="metric">
                    <label>Air Flow</label>
                    <strong>{results.gt.airFlow_kgs.toFixed(1)} kg/s</strong>
                  </div>
                  <div className="metric">
                    <label>Exhaust Temp</label>
                    <strong>{results.gt.exhaustT_C.toFixed(1)} °C</strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* T-s DIAGRAMS - NOW CORRECT */}
      {results && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Compressor Stages T-s Diagrams */}
          <div style={{ borderTop: '1px solid var(--color-border-secondary)', paddingTop: 20 }}>
            <h3>Centrifugal Compressor - Temperature-Entropy Diagrams</h3>
            <div style={{ display: 'grid', gridTemplateColumns: results.compStages.length === 1 ? '1fr' : '1fr 1fr', gap: 20, marginTop: 20 }}>
              {results.compStages.map(stage => (
                <div key={stage.stage} style={{ border: '1px solid var(--color-border-secondary)', padding: 15, borderRadius: 8 }}>
                  <h4 style={{ marginBottom: 10, textAlign: 'center' }}>Stage {stage.stage}</h4>
                  
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart margin={{ top: 20, right: 30, left: 80, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="s" 
                        type="number"
                        label={{ value: 'Entropy [J/(mol·K)]', position: 'insideBottomRight', offset: -10 }}
                        tickFormatter={v => v.toFixed(2)}
                      />
                      <YAxis 
                        label={{ value: 'Temperature [°C]', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        formatter={(value, name) => {
                          if (name === 'T') return [value.toFixed(1) + ' °C', 'Temperature'];
                          if (name === 's') return [value.toFixed(3) + ' J/(mol·K)', 'Entropy'];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      
                      {/* Isentropic path (vertical) */}
                      <Line 
                        data={stage.path_1_2s}
                        type="monotone"
                        dataKey="T"
                        stroke="#2196F3"
                        name="Isentropic (1→2s)"
                        dot={false}
                        strokeWidth={2}
                      />
                      
                      {/* Actual path (curved) */}
                      <Line 
                        data={stage.path_1_2}
                        type="monotone"
                        dataKey="T"
                        stroke="#FF6B35"
                        name="Actual (1→2)"
                        dot={false}
                        strokeWidth={2}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>

                  {/* Annotations */}
                  <div style={{ marginTop: 15, fontSize: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div style={{ padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                      <strong>State 1 (Inlet):</strong>
                      <div>T₁ = {stage.Tin.toFixed(1)} °C</div>
                      <div>P₁ = {stage.Pin.toFixed(1)} bar</div>
                      <div>S₁ = {stage.S_1.toFixed(3)} J/(mol·K)</div>
                    </div>
                    <div style={{ padding: 8, background: '#e3f2fd', borderRadius: 4 }}>
                      <strong>State 2s (Isentropic):</strong>
                      <div>T₂s = {(stage.T_2s - 273.15).toFixed(1)} °C</div>
                      <div>P₂ = {stage.Pout.toFixed(1)} bar</div>
                      <div>S₂s = {stage.S_2s.toFixed(3)} J/(mol·K)</div>
                    </div>
                    <div style={{ padding: 8, background: '#ffe0b2', borderRadius: 4 }}>
                      <strong>State 2 (Actual):</strong>
                      <div>T₂ = {stage.Tout.toFixed(1)} °C</div>
                      <div>P₂ = {stage.Pout.toFixed(1)} bar</div>
                      <div>S₂ = {stage.S_2.toFixed(3)} J/(mol·K)</div>
                    </div>
                    <div style={{ padding: 8, background: '#f3e5f5', borderRadius: 4 }}>
                      <strong>Efficiency:</strong>
                      <div>η_is = {(stage.eta_isentropic * 100).toFixed(1)}%</div>
                      <div>ΔS = {(stage.entropy_increase_J_molK * 1000).toFixed(1)} J/(mol·K) × 10⁻³</div>
                      <div>W_lost ≈ {(stage.irreversibility_loss / 1000).toFixed(2)} kJ/kmol</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gas Turbine (Brayton Cycle) T-s Diagrams */}
          <div style={{ borderTop: '1px solid var(--color-border-secondary)', paddingTop: 20 }}>
            <h3>Gas Turbine (Brayton Cycle) - Temperature-Entropy Diagrams</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
              {/* Compression Path */}
              <div style={{ border: '1px solid var(--color-border-secondary)', padding: 15, borderRadius: 8 }}>
                <h4 style={{ marginBottom: 10, textAlign: 'center' }}>Path 1→2: Compression</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart margin={{ top: 20, right: 30, left: 80, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="s" 
                      type="number"
                      label={{ value: 'Entropy [J/(mol·K)]', position: 'insideBottomRight', offset: -10 }}
                      tickFormatter={v => v.toFixed(2)}
                    />
                    <YAxis 
                      label={{ value: 'Temperature [°C]', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip formatter={(value) => value.toFixed(2)} />
                    <Legend />
                    
                    <Line 
                      data={results.gt.compression.isentropic}
                      type="monotone"
                      dataKey="T"
                      stroke="#2196F3"
                      name="Isentropic"
                      dot={false}
                      strokeWidth={2}
                    />
                    <Line 
                      data={results.gt.compression.actual}
                      type="monotone"
                      dataKey="T"
                      stroke="#FF6B35"
                      name="Actual"
                      dot={false}
                      strokeWidth={2}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
                <div style={{ marginTop: 10, fontSize: 11 }}>
                  <div>T₁ = {results.gt.compression.T1.toFixed(1)}°C, P = {results.gt.compression.P1.toFixed(2)} bar</div>
                  <div>T₂s = {results.gt.compression.T2s.toFixed(1)}°C, T₂ = {results.gt.compression.T2.toFixed(1)}°C</div>
                  <div>η_comp = {(gtParams.eta_comp * 100).toFixed(1)}%</div>
                </div>
              </div>

              {/* Expansion Path */}
              <div style={{ border: '1px solid var(--color-border-secondary)', padding: 15, borderRadius: 8 }}>
                <h4 style={{ marginBottom: 10, textAlign: 'center' }}>Path 3→4: Expansion</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart margin={{ top: 20, right: 30, left: 80, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="s" 
                      type="number"
                      label={{ value: 'Entropy [J/(mol·K)]', position: 'insideBottomRight', offset: -10 }}
                      tickFormatter={v => v.toFixed(2)}
                    />
                    <YAxis 
                      label={{ value: 'Temperature [°C]', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip formatter={(value) => value.toFixed(2)} />
                    <Legend />
                    
                    <Line 
                      data={results.gt.expansion.isentropic}
                      type="monotone"
                      dataKey="T"
                      stroke="#2196F3"
                      name="Isentropic"
                      dot={false}
                      strokeWidth={2}
                    />
                    <Line 
                      data={results.gt.expansion.actual}
                      type="monotone"
                      dataKey="T"
                      stroke="#FF6B35"
                      name="Actual"
                      dot={false}
                      strokeWidth={2}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
                <div style={{ marginTop: 10, fontSize: 11 }}>
                  <div>T₃ = {results.gt.expansion.T3.toFixed(1)}°C, P = {results.gt.expansion.P3.toFixed(2)} bar</div>
                  <div>T₄s = {results.gt.expansion.T4s.toFixed(1)}°C, T₄ = {results.gt.expansion.T4.toFixed(1)}°C</div>
                  <div>η_turb = {(gtParams.eta_turb * 100).toFixed(1)}%</div>
                </div>
              </div>

              {/* Constant Pressure Paths (Combustion & Exhaust) */}
              <div style={{ border: '1px solid var(--color-border-secondary)', padding: 15, borderRadius: 8 }}>
                <h4 style={{ marginBottom: 10, textAlign: 'center' }}>Path 2→3: Combustion (Constant P)</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart margin={{ top: 20, right: 30, left: 80, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="s" 
                      type="number"
                      label={{ value: 'Entropy [J/(mol·K)]', position: 'insideBottomRight', offset: -10 }}
                      tickFormatter={v => v.toFixed(2)}
                    />
                    <YAxis 
                      label={{ value: 'Temperature [°C]', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip formatter={(value) => value.toFixed(2)} />
                    <Line 
                      data={results.gt.combustion.path}
                      type="monotone"
                      dataKey="T"
                      stroke="#4CAF50"
                      name="Heat Addition"
                      dot={false}
                      strokeWidth={2}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
                <div style={{ marginTop: 10, fontSize: 11 }}>
                  <div>T₂ = {results.gt.combustion.T2.toFixed(1)}°C → T₃ = {results.gt.combustion.T3.toFixed(1)}°C</div>
                  <div>P = {results.gt.combustion.P.toFixed(2)} bar (constant)</div>
                </div>
              </div>

              {/* Complete Cycle */}
              <div style={{ border: '1px solid var(--color-border-secondary)', padding: 15, borderRadius: 8 }}>
                <h4 style={{ marginBottom: 10, textAlign: 'center' }}>Complete Brayton Cycle</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart margin={{ top: 20, right: 30, left: 80, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="s" 
                      type="number"
                      label={{ value: 'Entropy [J/(mol·K)]', position: 'insideBottomRight', offset: -10 }}
                      tickFormatter={v => v.toFixed(2)}
                    />
                    <YAxis 
                      label={{ value: 'Temperature [°C]', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip formatter={(value) => value.toFixed(2)} />
                    <Legend />
                    
                    <Line 
                      data={results.gt.compression.actual}
                      type="monotone"
                      dataKey="T"
                      stroke="#FF6B35"
                      name="Compression (1→2)"
                      dot={false}
                      strokeWidth={2}
                    />
                    <Line 
                      data={results.gt.combustion.path}
                      type="monotone"
                      dataKey="T"
                      stroke="#4CAF50"
                      name="Combustion (2→3)"
                      dot={false}
                      strokeWidth={2}
                    />
                    <Line 
                      data={results.gt.expansion.actual}
                      type="monotone"
                      dataKey="T"
                      stroke="#2196F3"
                      name="Expansion (3→4)"
                      dot={false}
                      strokeWidth={2}
                    />
                    <Line 
                      data={results.gt.exhaust.path}
                      type="monotone"
                      dataKey="T"
                      stroke="#9C27B0"
                      name="Exhaust (4→1)"
                      dot={false}
                      strokeWidth={2}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
                <div style={{ marginTop: 10, fontSize: 11 }}>
                  <div>Thermal Efficiency = {(results.gt.thermalEff * 100).toFixed(1)}%</div>
                  <div>Net Work = {(results.shaftPower_kW).toFixed(1)} kW</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
